import { useEffect } from 'react';
import Socket from "Socket/socket.js";
import {
  stateChangedUpdateEvent,
  devicesMessageTypeEvent,
  alarmsMessageTypeEvent,
} from "../constants/apiUrl";
import { fetchEdgeDataSocket } from "../ducks/devicesDuck";
import { fetchEventUpdateSocket } from "../ducks/systemDuck";
import { upsertAlarm } from "../ducks/alarmsDuck";
import { useRef } from 'react';

/*
  This file contains custom hooks for establishing socket connections to specific events.
  Each hook is responsible for setting up a listener for a particular event, handling incoming
  data, and removing the listener when the component is unmounted.

  Arguments these hooks need are:
     - socketConnected: a boolean indicating whether the socket is connected
     - systemId: the identifier for the system (this is "edge" in local edge environment)
     - dispatch: the Redux dispatch function
*/

export const useSocketConnectionDevicesMessageTypeEvent = (socketConnected, systemId, dispatch, processAsRawSensorData) => {

  const msgArray = useRef([]); // Bundle messages together before dispatch
  const msgTimer = useRef(null); // Timer to dispatch messages
  const msgTimeout = 1000; // Time to wait before dispatching messages
  const msgCount = 50; // Number of messages to bundle together


  useEffect(() => {
    if (socketConnected) {
      const devicesMessageTypeEventListener = (res) => {
        if (res && (systemId === res.DeviceId || systemId === "edge")) {
          // Push messages to mgsArray to bundle them
          msgArray.current.push(res);

          // Start the dipsatch timer if it's the first message
          if (msgArray.current.length === 1) {
            msgTimer.current = setTimeout(() => {
              if (msgArray.current.length > 0) {
                const messagesToSend = [...msgArray.current];
                // clear BEFORE dispatch
                msgArray.current = [];
                // use copied messages to avoid race condition where due to async nature of dispatch, 
                // msgArray.current could get additional messages in it before the dispatch is complete and we would lose those messages
                dispatch(fetchEdgeDataSocket({ systemId, data: messagesToSend, processAsRawSensorData }));
              }
            }, msgTimeout);
          }

          // Dispatch immediately if we reach msgCount
          if (msgArray.current.length >= msgCount) {
            clearTimeout(msgTimer.current); // Clear the timer if we hit msgCount first
            const messagesToSend = [...msgArray.current];
            msgArray.current = [];
            dispatch(fetchEdgeDataSocket({ systemId, data: messagesToSend, processAsRawSensorData }));
          }
        }
      };
      // console.log("Adding devicesMessageTypeEvent listener for systemId:", systemId);
      Socket.on(devicesMessageTypeEvent, devicesMessageTypeEventListener, systemId);
      return () => {
        // console.log("Removing devicesMessageTypeEvent listener for systemId:", systemId);
        Socket.removeListener(devicesMessageTypeEvent, devicesMessageTypeEventListener, systemId);
        clearTimeout(msgTimer.current);
      };
    }
  }, [socketConnected, systemId, dispatch]);
};

export const useSocketConnectionStateChangedUpdateEvent = (socketConnected, systemId, dispatch) => {
  useEffect(() => {
    if (socketConnected) {
      const stateChangedUpdateEventListener = (res) => {
        if (res && (systemId === res.DeviceId || systemId === "edge")) {
          dispatch(
            fetchEventUpdateSocket({
              systemId,
              data: res,
            })
          );
        }
      };
      Socket.on(stateChangedUpdateEvent, stateChangedUpdateEventListener, systemId);
      return () => {
        Socket.removeListener(stateChangedUpdateEvent, stateChangedUpdateEventListener, systemId);
      };
    }
  }, [socketConnected, systemId, dispatch]);
};

export const useSocketConnectionAlarmsMessageTypeEvent = (socketConnected, systemId, dispatch) => {
  useEffect(() => {
    if (socketConnected) {
      const alarmsMessageTypeEventListener = (res) => {
        if (res && (systemId === res.DeviceId || systemId === "edge")) {
          dispatch(
            upsertAlarm({
              systemId,
              data: res,
            })
          );
        }
      };
      Socket.on(alarmsMessageTypeEvent, alarmsMessageTypeEventListener, systemId);
      return () => {
        Socket.removeListener(alarmsMessageTypeEvent, alarmsMessageTypeEventListener, systemId);
      };
    }
  }, [socketConnected, systemId, dispatch]);
};
